import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowUpRight, X } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { FILTERS, PROJECTS } from "@/lib/site-data";

export default function ProjectGallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const visible = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="projects" data-testid="project-gallery-section" className="bg-[#0A0A0A] py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Reveal>
          <Overline>Portfolio</Overline>
          <div className="flex flex-wrap items-end justify-between gap-8 mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">See Our Work</h2>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`gallery-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-4.5 px-5 py-2 text-[13px] font-bold transition-[background-color,color,box-shadow] duration-300 ${
                    filter === f
                      ? "bg-[#0066FF] text-white shadow-[0_0_20px_rgba(0,102,255,0.45)]"
                      : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-5">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.button
                key={p.title}
                layout
                data-testid={`project-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4 }}
                onClick={() => setLightbox(p)}
                className="group relative mb-5 block w-full break-inside-avoid overflow-hidden rounded-2xl border border-white/10 text-left"
              >
                <img
                  src={p.img}
                  alt={p.title}
                  loading="lazy"
                  className={`w-full ${p.ratio} object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 p-6 translate-y-2 transition-transform duration-300 group-hover:translate-y-0">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-cyan-400">{p.category}</span>
                  <h3 className="font-display text-xl font-semibold text-white mt-1">{p.title}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-400">
                    <MapPin size={13} className="text-cyan-400/80" /> {p.city}, KY
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    View Project <ArrowUpRight size={15} className="text-cyan-400" />
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        <p className="mt-6 text-xs text-gray-500">
          Project photos shown are representative while the full Phillips project library is being added.
        </p>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="project-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[95] bg-black/92 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.img} alt={lightbox.title} className="w-full max-h-[75vh] object-cover rounded-2xl" />
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">{lightbox.category}</span>
                  <h3 className="font-display text-2xl font-semibold text-white">{lightbox.title}</h3>
                  <p className="flex items-center gap-1.5 text-sm text-gray-400 mt-1">
                    <MapPin size={14} className="text-cyan-400/80" /> {lightbox.city}, KY
                  </p>
                </div>
                <button
                  data-testid="project-lightbox-close"
                  onClick={() => setLightbox(null)}
                  className="rounded-full border border-white/20 p-3 text-white hover:border-cyan-400 hover:text-cyan-400 transition-colors duration-200"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
